/**
 * The sign-off queue's one shared store (Execution revamp step 4, EXV-037).
 *
 * The sidebar chip, the queue page and every place a counter-signature can be given read the same
 * `GET /bp/get_supervisor_approvals` answer through here. The server decides everything — which rows are the
 * caller's to sign (policy AND not the performer), the plant, the PENDING default, whether the caller may
 * counter-sign at all (`may_countersign`) — and this store only holds the last answer, single-flighted.
 * `invalidateSignoffQueue()` is called after any signature, so the chip never shows a stale count.
 */
import { useEffect, useSyncExternalStore } from 'react';
import Axios from 'axios';

const listeners = new Set();
let snapshot = { loaded: false, mayCountersign: false, pendingCount: 0, rows: [], refusal: null, failed: false };
let inflight = null;

const emit = () => listeners.forEach((l) => l());

const sessionToken = () => {
  try {
    return localStorage.getItem('Token') || '';
  } catch (e) {
    return '';
  }
};

/** The 403 body as a refusal the page can name ({ module, required, message }), or null. */
export const refusalFrom = (err) => {
  const res = err?.response;
  if (res?.status !== 403) return null;
  const body = res.data || {};
  return { module: body.module || null, required: body.required || null, message: body.message || body.detail || null };
};

/** `GET get_supervisor_approvals` with the caller's session; resolves with the body, rejects with the axios error. */
export const fetchSignoffApprovals = (params = {}) =>
  Axios.get('/bp/get_supervisor_approvals', {
    params,
    headers: { Authorization: `Bearer ${sessionToken()}` },
  }).then((res) => res.data);

export function loadSignoffQueue({ force = false } = {}) {
  if (inflight && !force) return inflight;
  const token = sessionToken();
  const request = fetchSignoffApprovals()
    .then((body) => {
      snapshot = {
        loaded: true,
        mayCountersign: !!body?.may_countersign,
        pendingCount: Number(body?.pending_count) || 0,
        rows: Array.isArray(body?.data) ? body.data : [],
        refusal: null,
        failed: false,
        token,
      };
    })
    .catch((err) => {
      snapshot = { loaded: true, mayCountersign: false, pendingCount: 0, rows: [], refusal: refusalFrom(err), failed: true, token };
    })
    .finally(() => {
      if (inflight === request) inflight = null;
      emit();
    });
  inflight = request;
  return request;
}

/** After a signature (or any act that changes the queue): re-read once and tell every subscriber. */
export function invalidateSignoffQueue() {
  return loadSignoffQueue({ force: true });
}

const subscribe = (l) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const getSnapshot = () => snapshot;

/** `{ loaded, mayCountersign, pendingCount, rows, refusal, failed }` — loads once on first use. */
export function useSignoffQueue({ enabled = true } = {}) {
  const state = useSyncExternalStore(subscribe, getSnapshot);
  useEffect(() => {
    // a new session (another login in this tab) re-reads; otherwise the answer is shared until invalidated
    if (enabled && (!snapshot.loaded || snapshot.token !== sessionToken()) && !inflight) loadSignoffQueue();
  }, [enabled]);
  return state;
}
